// NSARA Stores V2 — onglet "Aperçu" du workspace boutique (section 9).
//
// Répond à une seule question: "comment va CETTE boutique ?".
// Toutes les valeurs viennent de l'API, filtrées par shopId:
//   - /api/dashboard/summary?shopId=  (RPC dashboard_summary, agrégats SQL)
//   - /api/orders?shopId=             (commandes récentes + compteur)
// Aucune métrique décorative, aucun graphique inventé: si une donnée n'est pas
// disponible, l'élément n'est tout simplement pas affiché.
import React, { useCallback, useEffect, useState } from 'react';
import { getShopDashboard, getOrders } from '../../services/apiClient';
import { formatMoney, formatMoneyShort, formatWhen, ORDER_STATUS_LABELS } from './storeUtils';

const statusClass = (status) => {
  if (status === 'PENDING') return 'stores-badge stores-badge--low stores-badge--plain';
  if (status === 'COMPLETED') return 'stores-badge stores-badge--active';
  if (status === 'CANCELLED') return 'stores-badge stores-badge--paused';
  return 'stores-badge stores-badge--live';
};

export default function StoreOverviewTab({ shop, businessId, shopId, onOpenOrders }) {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!businessId || !shopId) return;
    setLoading(true);
    setError('');
    try {
      const [dash, ordersRes] = await Promise.all([
        getShopDashboard({ businessId, shopId }),
        getOrders({ businessId, shopId, limit: 50 })
      ]);
      setSummary(dash.summary || null);
      setOrders(ordersRes.orders || []);
    } catch (err) {
      setError(err?.message || 'Impossible de charger l’aperçu de cette boutique.');
    } finally {
      setLoading(false);
    }
  }, [businessId, shopId]);

  // shopId dans les dépendances: changer de boutique recharge tout et RÉINITIALISE
  // l'état avant l'appel — jamais de fusion avec les données de la boutique
  // précédente (section 8: "never leak prior shop products/orders").
  useEffect(() => { setSummary(null); setOrders([]); load(); }, [load]);

  if (error) return <p className="stores-alert stores-alert--error" role="alert">{error}</p>;

  const metrics = summary ? [
    { label: 'Chiffre d’affaires', value: formatMoneyShort(summary.revenue), note: `${summary.salesCount ?? 0} ventes` },
    { label: 'Commandes', value: String(orders.length), note: orders.length > 0 ? `dont ${orders.filter((o) => o.status === 'PENDING').length} nouvelles` : 'aucune commande' },
    { label: 'Produits', value: String(summary.productsCount ?? 0), note: 'dans cette boutique' },
    { label: 'Stock faible', value: String(summary.lowStockCount ?? 0), note: Number(summary.lowStockCount) > 0 ? 'à réapprovisionner' : 'tout est bon', warn: Number(summary.lowStockCount) > 0 }
  ] : [];

  return (
    <div className="stores-section">
      <div className="stores-greeting">
        <h2>Bonjour 👋</h2>
        <p>Voici ce qui se passe chez {shop?.name}.</p>
      </div>

      {loading && (
        <div className="stores-metrics" aria-busy="true" aria-label="Chargement des indicateurs">
          {[0, 1, 2, 3].map((key) => (
            <div key={key} className="stores-metric">
              <div className="stores-skeleton-line" style={{ width: '60%' }} />
              <div className="stores-skeleton-line" style={{ width: '40%', marginTop: 10 }} />
            </div>
          ))}
        </div>
      )}

      {!loading && metrics.length > 0 && (
        <div className="stores-metrics">
          {metrics.map((metric) => (
            <div key={metric.label} className={metric.warn ? 'stores-metric stores-metric--warn' : 'stores-metric'}>
              <p className="stores-metric-label">{metric.label}</p>
              <p className="stores-metric-value">{metric.value}</p>
              <p className="stores-metric-note">{metric.note}</p>
            </div>
          ))}
        </div>
      )}

      <div className="stores-panel">
        <div className="stores-section-head" style={{ marginBottom: 12 }}>
          <div>
            <h3 className="stores-panel-title" style={{ margin: 0 }}>Commandes récentes</h3>
            {!loading && orders.length === 0 && <p className="stores-section-sub">Aucune commande pour le moment.</p>}
          </div>
          {!loading && orders.length > 0 && (
            <button type="button" className="stores-btn stores-btn--ghost stores-btn--sm" onClick={onOpenOrders}>Tout voir</button>
          )}
        </div>

        {!loading && orders.length > 0 && (
          <div className="stores-order-list">
            {orders.slice(0, 5).map((order) => (
              <div className="stores-order-card" key={order.id}>
                <div className="stores-order-top">
                  <div>
                    <p className="stores-order-number">{order.order_number}</p>
                    <p className="stores-order-client">{order.customer_name}</p>
                  </div>
                  <span className={statusClass(order.status)}>{ORDER_STATUS_LABELS[order.status] || order.status}</span>
                </div>
                <div className="stores-order-foot">
                  <span className="stores-order-total">{formatMoney(order.subtotal_estimated)}</span>
                  <span className="stores-order-when">{formatWhen(order.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {!loading && summary && Number(summary.revenue) > 0 && (
        <div className="stores-panel">
          <h3 className="stores-panel-title">Activité commerciale</h3>
          <div className="stores-detail-grid">
            <div>
              <div className="stores-detail-item"><span>Ventes enregistrées</span><strong>{summary.salesCount ?? 0}</strong></div>
              <div className="stores-detail-item"><span>Panier moyen</span><strong>{formatMoney(summary.averageBasket)}</strong></div>
            </div>
            <div>
              <div className="stores-detail-item"><span>Valeur du stock</span><strong>{formatMoney(summary.stockValue)}</strong></div>
              <div className="stores-detail-item"><span>Factures</span><strong>{summary.invoicesCount ?? 0}</strong></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
